// CLI-local, deliberately-loose mirrors of the backend config + diff shapes.
//
// The CLI must not depend on @rekor/shared at runtime (it would pull the whole shared bundle
// into the published package). These structural types only need to serialize/deserialize YAML
// and render a diff, so they stay minimal. Source of truth: workers/shared/src/types/promotion.ts
// and the per-entity row shapes — the config round-trip test guards against drift.

export interface CollectionConfig {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  json_schema: Record<string, unknown>;
  ui?: Record<string, unknown>;
  sources?: unknown[];
}

export interface RelationshipTypeConfig {
  id: string;
  description?: string;
  data_schema?: unknown;
  source_collections?: string[];
  target_collections?: string[];
}

export interface InboundWebhookConfig {
  id: string;
  name: string;
  collection_scope?: string[] | null;
  /** Inline to_rekor mapping applied to the payload before write (#454 PR2). Secret-free. */
  field_mapping?: Record<string, unknown> | null;
  /** Reuse a collection source's field_mapping: {collection, source} (#454 PR2). */
  source_binding?: Record<string, unknown> | null;
  /** Static-header inbound auth: {type:'static_header',header} (#470). Secret-free. */
  ingest_auth?: Record<string, unknown> | null;
  /** Hydration config: {id_path,event_path?,event_map?,default_op?} (#470 PR2). Secret-free. */
  hydration?: Record<string, unknown> | null;
  enabled?: boolean;
}

export interface TriggerConfig {
  id: string;
  name: string;
  /** Polymorphic action (#390): {type:'webhook',url} or {type:'internal_write',...}. Secret omitted. */
  action?: Record<string, unknown>;
  /** Legacy flat webhook url — kept optional for back-compat; `action` is canonical. */
  url?: string;
  events: string[];
  collection_scope?: string[] | null;
  filter?: Record<string, unknown> | null;
  skip_inbound_webhook_writes?: boolean;
  skip_cascade_writes?: boolean;
  enabled?: boolean;
}

export interface EndpointConfig {
  id: string;
  name: string;
  description?: string;
  tools?: unknown[];
  relationships?: unknown[];
  batch?: unknown;
  sql_query?: boolean;
}

export interface DatabaseConfig {
  collections: CollectionConfig[];
  relationship_types: RelationshipTypeConfig[];
  inbound_webhooks: InboundWebhookConfig[];
  triggers: TriggerConfig[];
  endpoints: EndpointConfig[];
}

/** Persisted in `<db-folder>/database.yaml`. `database_id` is absent for an as-yet-uncreated preview. */
export interface DatabaseMeta {
  database_id?: string;
  origin_database_id?: string;
  name?: string;
  description?: string;
  environment?: string;
  // #488: external integration toggle (enabled | disabled). Informational on pull — toggle a preview
  // with `rekor databases update <preview> --integrations <mode>` (preview-only, enforced server-side).
  integrations?: string;
}

export interface DiffBucket<T> {
  added: T[];
  modified: { before: T; after: T }[];
  removed: T[];
}

export interface ConfigDiff {
  collections: DiffBucket<CollectionConfig>;
  relationship_types?: DiffBucket<RelationshipTypeConfig>;
  inbound_webhooks: DiffBucket<InboundWebhookConfig>;
  triggers: DiffBucket<TriggerConfig>;
  endpoints?: DiffBucket<EndpointConfig>;
}

/** The five config entity kinds, in a stable order used for folders, files and diff rendering. */
export const ENTITY_KINDS = ['collections', 'relationship_types', 'inbound_webhooks', 'triggers', 'endpoints'] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];

/** Folder name on disk for each entity kind (kebab-case for the hyphenated one). */
export const ENTITY_DIRS: Record<EntityKind, string> = {
  collections: 'collections',
  relationship_types: 'relationship-types',
  inbound_webhooks: 'inbound-webhooks',
  triggers: 'triggers',
  endpoints: 'endpoints',
};
